import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  CircularProgress,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CloseIcon from "@mui/icons-material/Close";
import DescriptionIcon from "@mui/icons-material/Description";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";

interface ReleaseNotesDialogProps {
  open: boolean;
  onClose: () => void;
}

interface ReleaseNote {
  date: string;
  title: string;
  content: string;
}

const ReleaseNotesDialog: React.FC<ReleaseNotesDialogProps> = ({
  open,
  onClose,
}) => {
  const [releaseNotes, setReleaseNotes] = useState<ReleaseNote[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | false>("panel0");

  useEffect(() => {
    if (open) {
      fetchReleaseNotes();
    }
  }, [open]);

  const fetchReleaseNotes = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch("/api/snowflake/release-notes");
      const data = await response.json();

      if (data.error) {
        setError(data.error);
        return;
      }

      // Parse markdown content
      const parsed = parseMarkdown(data.content);
      setReleaseNotes(parsed);
    } catch (err) {
      console.error("Error fetching release notes:", err);
      setError("Failed to load release notes");
    } finally {
      setLoading(false);
    }
  };

  const parseMarkdown = (content: string): ReleaseNote[] => {
    const notes: ReleaseNote[] = [];
    const lines = content.split("\n");
    let currentNote: ReleaseNote | null = null;
    let currentContent: string[] = [];

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];

      // Check for h2 headers (## Date - Title)
      if (line.startsWith("## ")) {
        // Save previous note if exists
        if (currentNote) {
          currentNote.content = currentContent.join("\n").trim();
          notes.push(currentNote);
        }

        // Parse new note header
        const headerText = line.substring(3).trim();
        const parts = headerText.split(" - ");
        currentNote = {
          date: parts[0] || "",
          title: parts.slice(1).join(" - ") || "",
          content: "",
        };
        currentContent = [];
      } else if (currentNote && line.trim() !== "" && !line.startsWith("# ")) {
        // Add content to current note
        currentContent.push(line);
      }
    }

    // Add last note
    if (currentNote) {
      currentNote.content = currentContent.join("\n").trim();
      notes.push(currentNote);
    }

    return notes;
  };

  const handleAccordionChange =
    (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpanded(isExpanded ? panel : false);
    };

  const formatContent = (content: string) => {
    return content.split("\n").map((line, index) => {
      // Handle headers
      if (line.startsWith("### ")) {
        return (
          <Typography
            key={index}
            variant="subtitle1"
            sx={{ fontWeight: 600, mt: 1.5, mb: 0.5, color: "#1976d2" }}
          >
            {line.substring(4)}
          </Typography>
        );
      }
      // Handle list items
      if (line.startsWith("- ")) {
        return (
          <Typography
            key={index}
            variant="body2"
            sx={{ ml: 2, mb: 0, color: "#555", lineHeight: 1.6 }}
          >
            • {line.substring(2)}
          </Typography>
        );
      }
      // Handle regular text
      if (line.trim()) {
        return (
          <Typography key={index} variant="body2" sx={{ mb: 0.5 }}>
            {line}
          </Typography>
        );
      }
      return null;
    });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: "90vh",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          bgcolor: "#f5f5f5",
          borderBottom: "1px solid #e0e0e0",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <DescriptionIcon sx={{ color: "#1976d2" }} />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Release Notes
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            color: "#666",
            "&:hover": { bgcolor: "#e0e0e0" },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 3, bgcolor: "#fafafa" }}>
        {loading && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              py: 4,
            }}
          >
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Typography color="error" sx={{ textAlign: "center", py: 4 }}>
            {error}
          </Typography>
        )}

        {!loading && !error && releaseNotes.length === 0 && (
          <Typography sx={{ textAlign: "center", py: 4, color: "#666" }}>
            No release notes available
          </Typography>
        )}

        {!loading && !error && releaseNotes.length > 0 && (
          <Box>
            {releaseNotes.map((note, index) => (
              <Accordion
                key={index}
                expanded={expanded === `panel${index}`}
                onChange={handleAccordionChange(`panel${index}`)}
                sx={{
                  mt: index === 0 ? 1.5 : 0,
                  mb: 1.5,
                  borderRadius: 1,
                  "&:before": { display: "none" },
                  boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    bgcolor: "#e9ecef",
                    borderRadius: 1,
                    "&:hover": { bgcolor: "#dee2e6" },
                    transition: "background-color 0.2s",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <CalendarTodayIcon sx={{ fontSize: 18, color: "#666" }} />
                    <Typography sx={{ fontWeight: 600, color: "#333" }}>
                      {note.date} - {note.title}
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ bgcolor: "white", p: 2 }}>
                  {formatContent(note.content)}
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          py: 2,
          bgcolor: "#f5f5f5",
          borderTop: "1px solid #e0e0e0",
        }}
      >
        <Button onClick={onClose} color="primary" variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ReleaseNotesDialog;
